import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search } from 'lucide-react';
import NewProjectModal from './NewProjectModal';
import GlobalSearchDropdown from './GlobalSearchDropdown';

/**
 * Shell del modulo de Proyectos.
 * Navbar nivel 1 con tabs (Proyectos / Mis tareas / Calendario) + buscar + CTA.
 * El contenido lo renderiza el Outlet de react-router.
 */
const ProyectosView = () => {
  const navigate = useNavigate();
  const [showNewModal, setShowNewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Dropdown global cross-proyecto. Aparece al enfocar el input con >=2 chars.
  // El searchTerm sigue propagandose via Outlet context — las vistas hijas mantienen
  // su filtrado in-line. Solo el dropdown reacciona al focus.
  const [searchFocused, setSearchFocused] = useState(false);
  const searchWrapperRef = useRef(null);

  const handleProyectoCreado = useCallback((proyecto) => {
    setShowNewModal(false);
    if (proyecto?.slug) {
      navigate(`/proyectos/${proyecto.slug}`);
    }
  }, [navigate]);

  // Click afuera cierra el dropdown sin limpiar el input.
  useEffect(() => {
    if (!searchFocused) return;
    const handler = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchFocused]);

  // Tras seleccionar un resultado: cerrar dropdown + limpiar input (la inline-filter
  // de la vista de destino se vuelve "Todos", no la busqueda anterior, lo que evita
  // sorpresa al aterrizar con un filtro residual).
  const handleResultSelected = useCallback(() => {
    setSearchFocused(false);
    setSearchTerm('');
  }, []);

  const showSearchDropdown = searchFocused && searchTerm.trim().length >= 2;

  const tabBase = "relative px-3 py-1.5 text-sm font-medium tracking-tight transition-colors duration-150";
  const tabIdle = "text-slate-500 hover:text-slate-900";
  const tabActive = "text-slate-900";

  return (
    <div className="bg-[#FAFAFA] flex flex-col -m-4 md:-m-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[calc(100%+2rem)] md:h-[calc(100%+4rem)] overflow-hidden">
      {/* Navbar nivel 1 */}
      <header className="flex-shrink-0 bg-white border-b border-slate-200/80">
        <div className="flex items-center justify-between h-[56px] px-6">
          {/* Tabs */}
          <nav className="flex items-center gap-1 -mb-px">
            {/* Tab "Métricas" oculta — el componente y la ruta /proyectos/metricas
                siguen existiendo (no se eliminó código). Re-habilitar cuando se
                redefina el approach del dashboard inteligente. */}

            <NavLink
              to="/proyectos"
              end
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Proyectos
                  {isActive && (
                    <motion.div
                      layoutId="proyectos-tab-underline"
                      className="absolute left-2 right-2 -bottom-px h-[2px] bg-cyan-500 rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/proyectos/mis-tareas"
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Mis tareas
                  {isActive && (
                    <motion.div
                      layoutId="proyectos-tab-underline"
                      className="absolute left-2 right-2 -bottom-px h-[2px] bg-cyan-500 rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/proyectos/calendario"
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Calendario
                  {isActive && (
                    <motion.div
                      layoutId="proyectos-tab-underline"
                      className="absolute left-2 right-2 -bottom-px h-[2px] bg-cyan-500 rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/proyectos/gantt"
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Gantt
                  {isActive && (
                    <motion.div
                      layoutId="proyectos-tab-underline"
                      className="absolute left-2 right-2 -bottom-px h-[2px] bg-cyan-500 rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/proyectos/roadmap"
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Roadmap
                  {isActive && (
                    <motion.div
                      layoutId="proyectos-tab-underline"
                      className="absolute left-2 right-2 -bottom-px h-[2px] bg-cyan-500 rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/proyectos/plantillas"
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Plantillas
                  {isActive && (
                    <motion.div
                      layoutId="proyectos-tab-underline"
                      className="absolute left-2 right-2 -bottom-px h-[2px] bg-cyan-500 rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          </nav>

          {/* Acciones */}
          <div className="flex items-center gap-3">
            <div ref={searchWrapperRef} className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Buscar en proyectos y tareas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                title="Búsqueda global: proyectos y tareas en todos los proyectos"
                className="w-64 pl-8 pr-3 py-1.5 text-sm bg-slate-100/70 border border-transparent rounded-md placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors"
              />
              {showSearchDropdown && (
                <GlobalSearchDropdown
                  query={searchTerm}
                  onClose={() => setSearchFocused(false)}
                  onResultSelected={handleResultSelected}
                />
              )}
            </div>

            <button
              onClick={() => setShowNewModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md transition-colors active:scale-[0.98]"
            >
              <Plus size={14} strokeWidth={2.5} />
              Nuevo proyecto
            </button>
          </div>
        </div>
      </header>

      {/* Contenido */}
      <main className="flex-1 overflow-y-auto">
        <Outlet context={{
          searchTerm,
          setSearchTerm,
          openNewProject: () => setShowNewModal(true),
        }} />
      </main>

      <NewProjectModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={handleProyectoCreado}
      />
    </div>
  );
};

export default ProyectosView;
